/**
 * LLM Provider metadata — shared between ArchitectureEnvironment and ApiKeyWizard.
 */

export type LLMProvider = 'gemini' | 'claude' | 'openai' | 'xai';

export interface ModelOption {
  value: string;
  label: string;
}

export interface LLMProviderConfig {
  id: LLMProvider;
  name: string;
  color: string;           // Tailwind color name (blue, orange, green, red)
  activeColor: string;     // Tailwind bg class for active state glow
  keyPlaceholder: string;
  description: string;
  localStorageKey: string;
  localStorageModel: string;
  envKey: string;
  defaultModel: string;
  models: ModelOption[];
  needsProxy?: boolean;
  proxyStorageKey?: string;
  defaultProxy?: string;
}

export const LLM_PROVIDER_CONFIGS: LLMProviderConfig[] = [
  {
    id: 'gemini',
    name: 'Google Gemini',
    color: 'blue',
    activeColor: 'bg-blue-500/10 border-blue-500/50',
    keyPlaceholder: 'Paste your Google Cloud API key here...',
    description: 'The Hookhouse Pro engine requires a valid Gemini API key for session synthesis.',
    localStorageKey: 'gemini_api_key',
    localStorageModel: 'gemini_model',
    envKey: 'VITE_GEMINI_API_KEY',
    defaultModel: 'gemini-3-pro-preview',
    models: [
      { value: 'gemini-3-pro-preview', label: 'Gemini 3 Pro (Recommended)' },
      { value: 'gemini-3-flash-preview', label: 'Gemini 3 Flash (Fast)' },
      { value: 'gemini-2.5-flash-lite-latest', label: 'Flash Lite (Low Quota)' },
    ],
  },
  {
    id: 'claude',
    name: 'Anthropic Claude',
    color: 'orange',
    activeColor: 'bg-orange-500/10 border-orange-500/50',
    keyPlaceholder: 'sk-ant-api03-...',
    description: 'Configure Anthropic Claude API. Requires proxy to bypass CORS.',
    localStorageKey: 'anthropic_api_key',
    localStorageModel: 'claude_model',
    envKey: 'VITE_ANTHROPIC_API_KEY',
    defaultModel: 'claude-sonnet-4-5-20250929',
    models: [
      { value: 'claude-sonnet-4-5-20250929', label: 'Claude Sonnet 4.5 (Recommended)' },
      { value: 'claude-haiku-4-5-20251001', label: 'Claude Haiku 4.5 (Fast)' },
      { value: 'claude-opus-4-5-20251101', label: 'Claude Opus 4.5 (Most Capable)' },
      { value: 'claude-3-5-sonnet-20241022', label: 'Claude 3.5 Sonnet' },
      { value: 'claude-3-5-haiku-20241022', label: 'Claude 3.5 Haiku' },
    ],
    needsProxy: true,
    proxyStorageKey: 'anthropic_proxy_url',
    defaultProxy: 'https://anthropic-proxy.frobinson47.workers.dev',
  },
  {
    id: 'openai',
    name: 'OpenAI',
    color: 'green',
    activeColor: 'bg-green-500/10 border-green-500/50',
    keyPlaceholder: 'sk-proj-...',
    description: 'Configure OpenAI API. No proxy required — calls go direct from browser.',
    localStorageKey: 'openai_api_key',
    localStorageModel: 'openai_model',
    envKey: 'VITE_OPENAI_API_KEY',
    defaultModel: 'gpt-4o',
    models: [
      { value: 'gpt-4o', label: 'GPT-4o (Recommended)' },
      { value: 'gpt-4o-mini', label: 'GPT-4o Mini (Fast)' },
      { value: 'o3-mini', label: 'o3-mini (Reasoning)' },
      { value: 'o1', label: 'o1 (Advanced Reasoning)' },
    ],
  },
  {
    id: 'xai',
    name: 'xAI Grok',
    color: 'red',
    activeColor: 'bg-red-500/10 border-red-500/50',
    keyPlaceholder: 'xai-...',
    description: 'Configure xAI Grok API. No proxy required — calls go direct from browser.',
    localStorageKey: 'xai_api_key',
    localStorageModel: 'xai_model',
    envKey: 'VITE_XAI_API_KEY',
    defaultModel: 'grok-4-1-fast-reasoning',
    models: [
      { value: 'grok-4-1-fast-reasoning', label: 'Grok 4.1 Fast Reasoning (Recommended)' },
    ],
  },
];

export function getProviderConfig(id: LLMProvider): LLMProviderConfig {
  return LLM_PROVIDER_CONFIGS.find(p => p.id === id)!;
}
